import { Routes } from '@angular/router';
import { authGuard, publicOnlyGuard } from './core/auth/auth.guard';
import { featureGuard } from './core/features/feature.guard';

export const routes: Routes = [
  {
    path: 'login',
    canActivate: [publicOnlyGuard],
    loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginPage),
  },
  {
    path: 'forgot-password',
    canActivate: [publicOnlyGuard],
    loadComponent: () =>
      import('./pages/forgot-password/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    path: 'reset-password',
    loadComponent: () =>
      import('./pages/reset-password/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    path: '',
    canActivate: [authGuard],
    loadComponent: () => import('./layout/shell').then((m) => m.Shell),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import('./pages/dashboard/dashboard.page').then((m) => m.DashboardPage),
      },
      {
        path: 'profile',
        loadComponent: () =>
          import('./pages/profile/profile.page').then((m) => m.ProfilePage),
      },

      // Help — available to any signed-in user
      {
        path: 'help',
        loadComponent: () => import('./pages/help/help.page').then((m) => m.HelpPage),
      },
      {
        path: 'help/:slug',
        loadComponent: () => import('./pages/help/help.page').then((m) => m.HelpPage),
      },

      // People
      {
        path: 'people',
        canActivate: [featureGuard('people', 'PersonRead')],
        loadComponent: () =>
          import('./pages/people/people-list/people-list.page').then((m) => m.PeopleListPage),
      },
      {
        path: 'people/:id',
        canActivate: [featureGuard('people', 'PersonRead')],
        loadComponent: () =>
          import('./pages/people/person-detail.page').then((m) => m.PersonDetailPage),
      },

      // Cases
      {
        path: 'cases',
        canActivate: [featureGuard('cases', 'CaseRead')],
        loadComponent: () =>
          import('./pages/cases/cases-list.page').then((m) => m.CasesListPage),
      },
      {
        path: 'cases/:id',
        canActivate: [featureGuard('cases', 'CaseRead')],
        loadComponent: () =>
          import('./pages/cases/case-detail.page').then((m) => m.CaseDetailPage),
      },

      // Interactions
      {
        path: 'interactions',
        canActivate: [featureGuard('interactions', 'InteractionRead')],
        loadComponent: () =>
          import('./pages/interactions/interactions-list.page').then((m) => m.InteractionsListPage),
      },

      // Organisations
      {
        path: 'organizations',
        canActivate: [featureGuard('organizations', 'OrganizationRead')],
        loadComponent: () =>
          import('./pages/organizations/organizations-list.page').then((m) => m.OrganizationsListPage),
      },
      {
        path: 'organizations/:id',
        canActivate: [featureGuard('organizations', 'OrganizationRead')],
        loadComponent: () =>
          import('./pages/organizations/organization-detail.page').then((m) => m.OrganizationDetailPage),
      },

      // Parents & Carers
      {
        path: 'parents-carers',
        canActivate: [featureGuard('parentsCarers', 'ParentCarerRead')],
        loadComponent: () =>
          import('./pages/parents-carers/parents-carers-list.page').then((m) => m.ParentsCarersListPage),
      },
      {
        path: 'parents-carers/:id',
        canActivate: [featureGuard('parentsCarers', 'ParentCarerRead')],
        loadComponent: () =>
          import('./pages/parents-carers/parent-carer-detail.page').then((m) => m.ParentCarerDetailPage),
      },

      // WT4C
      {
        path: 'wt4c',
        canActivate: [featureGuard('wt4c', 'ClassRead')],
        loadComponent: () =>
          import('./pages/wt4c/wt4c-hub.page').then((m) => m.Wt4cHubPage),
      },
      {
        path: 'wt4c/courses/:id',
        canActivate: [featureGuard('wt4c', 'ClassRead')],
        loadComponent: () =>
          import('./pages/wt4c/wt4c-course.page').then((m) => m.Wt4cCoursePage),
      },
      {
        path: 'wt4c/service-users/:id',
        canActivate: [featureGuard('wt4c', 'Wt4cServiceUserRead')],
        loadComponent: () =>
          import('./pages/wt4c/wt4c-service-user.page').then((m) => m.Wt4cServiceUserPage),
      },

      // Admin
      {
        path: 'admin/reference',
        canActivate: [featureGuard('adminReference', 'ReferenceDataManage')],
        loadComponent: () =>
          import('./pages/admin/reference-data/reference-data.page').then(
            (m) => m.ReferenceDataPage,
          ),
      },
      {
        path: 'admin/users',
        canActivate: [featureGuard('adminUsers', 'UserManage')],
        loadComponent: () =>
          import('./pages/admin/users/users.page').then((m) => m.UsersAdminPage),
      },
      {
        path: 'admin/audit',
        canActivate: [featureGuard('adminAudit', 'AuditRead')],
        loadComponent: () =>
          import('./pages/admin/audit-log/audit-log.page').then((m) => m.AuditLogPage),
      },
      {
        path: 'admin/custom-fields',
        canActivate: [featureGuard('adminCustomFields', 'CustomFieldManage')],
        loadComponent: () =>
          import('./pages/admin/custom-fields/custom-fields.page').then((m) => m.CustomFieldsAdminPage),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
